import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import { clientApi } from "../services/api";
import {
  Client,
  ClientCreate,
  ClientFilters,
  ClientUpdate,
} from "../types/client";

export const useClients = (filters: ClientFilters = {}) => {
  return useQuery(["clients", filters], () => clientApi.getClients(filters), {
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });
};

export const useClient = (id: number) => {
  return useQuery(["client", id], () => clientApi.getClient(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  return useMutation((data: ClientCreate) => clientApi.createClient(data), {
    onSuccess: (client: Client) => {
      queryClient.invalidateQueries(["clients"]);
      toast.success(`Client "${client.name}" created`);
    },
    onError: (error: any) => {
      const msg = error.response?.data?.detail || "Failed to create client";
      toast.error(msg);
    },
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: ClientUpdate }) =>
      clientApi.updateClient(id, data),
    {
      onSuccess: (client: Client) => {
        queryClient.setQueryData(["client", client.id], client);
        queryClient.invalidateQueries(["clients"]);
        toast.success(`Client "${client.name}" updated`);
      },
      onError: (error: any) => {
        const msg = error.response?.data?.detail || "Failed to update client";
        toast.error(msg);
      },
    },
  );
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => clientApi.deleteClient(id), {
    onSuccess: (_: void, id: number) => {
      queryClient.removeQueries(["client", id]);
      queryClient.invalidateQueries(["clients"]);
      toast.success("Client deleted");
    },
    onError: (error: any) => {
      const msg = error.response?.data?.detail || "Failed to delete client";
      toast.error(msg);
    },
  });
};
